<template>
  <div data-app>
    <div
      role="alert"
      v-bind:class="{ show: errors.length }"
      class="alert fade alert-danger"
    >
      <div class="alert-text" v-for="(error, i) in errors" :key="i">
        {{ error }}
      </div>
    </div>
    <v-card>
      <v-card-title>Websites</v-card-title>
      <v-data-table
        v-model="selected"
        :loading="loading"
        :headers="headers"
        :items="websites"
        :search="search"
        :footer-props="{
          itemsPerPageOptions: [5, 10, 50, 100, 250, -1], // -> Add this example
          showFirstLastPage: false,
          firstIcon: 'mdi-arrow-collapse-left',
          lastIcon: 'mdi-arrow-collapse-right',
          prevIcon: 'mdi-chevron-left',
          nextIcon: 'mdi-chevron-right',
        }"
        item-key="id"
        class="elevation-1"
      >
        <template v-slot:top>
          <v-toolbar flat color="white">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Search"
              single-line
              hide-details
            ></v-text-field>
            <v-spacer></v-spacer>
            <v-dialog v-model="dialog" max-width="500px">
              <template v-slot:activator="{ on }">
                <v-btn color="primary" dark class="mb-2" v-on="on"
                  >New Website</v-btn
                >
              </template>
              <v-card>
                <v-card-title>
                  <span class="headline">{{ formTitle }}</span>
                </v-card-title>

                <v-card-text>
                  <v-form ref="newForm" lazy-validation>
                    <v-container>
                      <v-row dense>
                        <v-col cols="8">
                          <div class="v-text-field__slot">
                            <v-label
                              style="left: 0px; right: auto; position: absolute"
                              >Server Names</v-label
                            >
                            <vue-tags-input
                              v-if="editedIndex != -1"
                              v-model="server_name"
                              :tags="editedItem.server_names"
                              :readonly="true"
                              placeholder="Current names"
                            />
                            <vue-tags-input
                              v-else
                              v-model="server_name"
                              :tags="editedItem.server_names"
                              placeholder="Add server names"
                              @tags-changed="updateServerNames"
                            />
                          </div>
                        </v-col>
                        <v-col cols="8">
                          <v-label
                            style="left: 0px; right: auto; position: absolute"
                            >Upstream Settings</v-label
                          >
                          <v-text-field
                            type="number"
                            v-model="
                              editedItem.upstream_settings.connect_timeout_s
                            "
                            label="Connect Timeout"
                            :max=60
                            :min=1
                            :rules="validConnectTimeout"
                          />
                          <v-text-field
                            type="number"
                            v-model="
                              editedItem.upstream_settings.idle_timeout_s
                            "
                            label="Idle Timeout"
                            :max=400
                            :min=1
                            :rules="validIdleTimeout"
                          />
                        </v-col>
                        <v-col cols="8">
                          <v-label
                            style="left: 0px; right: auto; position: absolute"
                            >Other Settings</v-label
                          >
                          <v-text-field
                            v-model="editedItem.description"
                            label="Description"
                            :rules="[v => (v.length <= 255) || 'Maximum 255 characters']"
                            :clearable = true
                          ></v-text-field>
                          <v-checkbox
                            v-model="editedItem.redirect_https"
                            label="Redirect HTTPS"
                          />
                          <v-checkbox
                            v-model="editedItem.transparent_mode"
                            label="Transparent"
                          />
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-form>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="blue darken-1" text @click="close"
                    >Cancel</v-btn
                  >
                  <v-btn color="blue darken-1" text @click="save">Save</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-toolbar>
        </template>
        <template v-slot:item.more="{ item }">
          <div>
            <b-dropdown id="dropdown-1" text="View..." :boundary="'viewport'">
              <b-dropdown-item @click="viewUpstreams(item)"
                v-if="!item.transparent_mode"
                >Upstreams</b-dropdown-item
              >
              <b-dropdown-item @click="viewListeners(item)"
                >Listeners</b-dropdown-item
              >
              <b-dropdown-item @click="viewFirewall(item)"
                >Firewall Rules</b-dropdown-item
              >
              <b-dropdown-item @click="viewPageSecurity(item)"
                >Page Security</b-dropdown-item
              >
              <b-dropdown-item @click="viewPageSecurityScripts(item)"
                >Page Security Scripts</b-dropdown-item
              >
            </b-dropdown>
          </div>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon small class="mr-2" @click="editItem(item)" v-on="on">mdi-pencil</v-icon>
            </template>
            <span>Edit</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon small class="mr-2" @click="deleteItem(item)" v-on="on">mdi-delete</v-icon>
            </template>
            <span>Delete</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon small class="mr-2" @click="purgeCache(item)" v-on="on">mdi-sync</v-icon>
            </template>
            <span>Purge Cache</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import api from "@/common/api.service";
import Errors from "@/common/errors";
import VueTagsInput from "@johmun/vue-tags-input";

export default {
  mounted() {
    api
      .query("beta/waf/websites")
      .then((response) => {
        this.websites = response.data.websites;
      })
      .catch((error) => this.errors.push(...Errors.extractErrorMessage(error)))
      .finally(() => (this.loading = false));
  },

  components: {
    VueTagsInput,
  },

  data: () => ({
    selected: [],
    server_name: "",
    headers: [
      { text: "ID", sortable: false, align: "left", value: "id" },
      { text: "Server Names", value: "server_names" },
      { text: "Redirect HTTPS", value: "redirect_https" },
      { text: "Description", value: "description" },
      { text: "More", value: "more", sortable: false },
      { text: "Actions", value: "actions", sortable: false },
    ],
    websites: [],
    errors: [],
    loading: true,
    dialog: false,
    editedIndex: -1,
    editedItem: {
      server_names: [],
      redirect_https: false,
      upstreams: [],
      listeners: [],
      upstream_settings: {
        connect_timeout_s: 60,
        idle_timeout_s: 100,
      },
      transparent_mode: true,
      description: "",
    },
    defaultItem: {
      server_names: [],
      redirect_https: false,
      upstreams: [],
      listeners: [],
      upstream_settings: {
        connect_timeout_s: 60,
        idle_timeout_s: 100,
      },
      transparent_mode: true,
      description: "",
    },
    search: "",
    validConnectTimeout: [
      (v) => !!v || "Connect timeout is required",
      (v) =>
        !v ||
        (parseInt(v) !== NaN && parseInt(v) > 0 && parseInt(v) <= 60) ||
        "Must be between 1 and 100",
    ],
    validIdleTimeout: [
      (v) => !!v || "Idle timeout is required",
      (v) =>
        !v ||
        (parseInt(v) !== NaN && parseInt(v) > 0 && parseInt(v) <= 400) ||
        "Must be between 1 and 100",
    ],
    validationError: null
  }),

  watch: {
    dialog(val) {
      val || this.close();
    },
  },

  methods: {
    updateServerNames(newNames) {
      this.editedItem.server_names = newNames;
      this.validationError = null; // Clear previous errors on change
    },

    validateTags(tags) {
      if (tags.length === 0 || tags.some(tag => !tag)) {
        this.validationError = 'Value is required';
        return false;
      }
      return true;
    },

    viewListeners(item) {
      let website_name = item.server_names[0]
      this.$store.commit('updateWebsiteName', website_name);
      this.$router.push({ name: "listeners", params: { website_id: item.id } });
    },

    viewUpstreams(item) {
      let website_name = item.server_names[0]
      this.$store.commit('updateWebsiteName', website_name);
      this.$router.push({ name: "upstreams", params: { website_id: item.id } });
    },

    viewFirewall(item) {
      let website_name = item.server_names[0]
      this.$store.commit('updateWebsiteName', website_name);
      this.$router.push({ name: "waf_rules", params: { website_id: item.id } });
    },

    viewPageSecurity(item) {
      let website_name = item.server_names[0]
      this.$store.commit('updateWebsiteName', website_name);
      this.$router.push({ name: "page_security", params: { website_id: item.id } });
    },

    viewPageSecurityScripts(item) {
      let website_name = item.server_names[0]
      this.$store.commit('updateWebsiteName', website_name);
      this.$router.push({ name: "page_security_scripts", params: { website_id: item.id } });
    },

    editItem(item) {
      this.editedIndex = this.websites.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.dialog = true;
    },

    deleteItem(item) {
      if (confirm("Are you sure you want to delete this website?")) {
        api.delete("beta/waf/websites/" + item.id).then((_response) => {
          const index = this.websites.indexOf(item);
          this.websites.splice(index, 1);
        });
      }
    },

    purgeCache(item) {
      if (
        confirm("Are you sure you want to purge the cache for this website?")
      ) {
        api.delete("beta/waf/websites/" + item.id + "/cache");
      }
    },

    close() {
      this.dialog = false;
      setTimeout(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      }, 300);
    },

    save() {
      if (!this.$refs.newForm.validate()) return;
      if (!this.validateTags(this.editedItem.server_names)) return;

      let newWebsite = {
        server_names: [],
        redirect_https: this.editedItem.redirect_https,
        // Force the user to add upstreams/listeners via a separate view, this
        // simplifies things
        upstreams: [],
        listeners: [],
        upstream_settings: this.editedItem.upstream_settings,
        transparent_mode: this.editedItem.transparent_mode,
        description: this.editedItem.description,
      };

      let editWebsite = {
        description: this.editedItem.description,
        redirect_https: this.editedItem.redirect_https,
        transparent_mode: this.editedItem.transparent_mode,
      };

      this.editedItem.server_names.map((elem) => {
        newWebsite.server_names.push(elem.text != null ? elem.text : elem);
      });

      if (this.editedIndex > -1) {
        api
          .patch(`beta/waf/websites/${this.editedItem.id}`, editWebsite)
          .then((response) => {
            const updatedWebsite = response.data;
            Object.assign(
              this.websites.find((val) => val.id == updatedWebsite.id),
              updatedWebsite
            );

            if ( updatedWebsite.upstream_settings != this.editedItem.upstream_settings ) {
              api
                .patch(
                  `beta/waf/websites/${this.editedItem.id}/upstream_settings`,
                  this.editedItem.upstream_settings
                )
                .then((response) => {
                  this.websites.find(
                    (val) => val.id == updatedWebsite.id
                  ).upstream_settings = response.data;
                })
                .catch((error) => this.errors.push(...Errors.extractErrorMessage(error)));
            }
          })
          .catch((error) => this.errors.push(...Errors.extractErrorMessage(error)));
      } else {
        api
          .post("beta/waf/websites", newWebsite)
          .then((response) => {
            this.websites = [response.data, ...this.websites];
          })
          .catch((error) => this.errors.push(...Errors.extractErrorMessage(error)));
      }

      this.close();
    },
  },

  computed: {
    formTitle() {
      return this.editedIndex === -1 ? "New Website" : "Edit Website";
    },
  },
};
</script>
